import axios from "axios";
import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { apiCall } from "../Apis/apiCall";
import LoadingComponent from "./ui/LoadingComponent";
import CustomSelect from "./ui/CustomSelect"; // Import CustomSelect
import { handleApiResponse, handleCustomResponse } from "../utils/response";

const EditUser = () => {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [roleId, setRoleId] = useState("");
  const [status, setStatus] = useState(false);
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [updatedLoading, setUpdatedLoading] = useState(false);
  const [userLoading, setUserLoading] = useState(false);
  const navigate = useNavigate();
  const { userId } = useParams(); // assuming you pass :userId in the route

  // Status options
  const statusOptions = [
    { value: true, label: "Active" },
    { value: false, label: "Inactive" }
  ];

  const fetchRoles = async () => {
    try {
      setRolesLoading(true);

      const res = await apiCall("get", "/get-all-roles", null, navigate);

      const data = res?.data?.data || res?.data?.roles || [];
      setRoles(data);
      setRolesLoading(false);

      const handleRes = res.data;
      handleApiResponse(handleRes, "success", "GET");
    } catch (err) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "GET");
      setRolesLoading(false);
    }
  };

  const fetchUserDetails = async () => {
    setUserLoading(true);
    try {
      const res = await apiCall(
        "get",
        `/get-user-by-id/${userId}`,
        null,
        navigate
      );

      const user = res?.data?.data;

      if (user) {
        setUsername(user.username);
        setEmail(user.email);
        setRoleId(user.role_id);
        setStatus(user.is_active);

        const handleRes = res.data;
        handleApiResponse(handleRes, "success", "GET");
      } else {
        handleCustomResponse("error", "User not found");
      }
    } catch (err) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "GET");
    } finally {
      setUserLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
    fetchUserDetails();
  }, []);

  const handleUpdateUser = async () => {
    setUpdatedLoading(true);

    if (!username.trim() || !email.trim() || !roleId) {
      handleCustomResponse("error", "All fields are required.");
      setUpdatedLoading(false);
      return;
    }

    try {
      const res = await apiCall(
        "put",
        `/update-user/${userId}`,
        { username, email, role_id: roleId, is_active: status },
        navigate
      );

      if (res?.data?.success) {
        const handleRes = res.data;
        handleApiResponse(handleRes, "success", "PUT");
        setTimeout(() => navigate("/all-users"), 2000);
      } else {
        handleCustomResponse("error", "Failed to update user.");
      }
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "PUT");
    } finally {
      setUpdatedLoading(false);
    }
  };

  if (userLoading) {
    return (
      <>
        <LoadingComponent />
      </>
    );
  }

  return (
    <div style={{ overflow: 'visible' }}>
      <div className="card h-100 p-0 radius-12" style={{ overflow: 'visible' }}>
        <div className="card-body p-40" style={{ overflow: 'visible' }}>
          {/* <h4 className="mb-4">Edit User</h4> */}
          <form>
            <div className="row">
              <div className="col-sm-6 mb-20">
                <label className="form-label">Username</label>
                <input
                  type="text"
                  className="form-control radius-8"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div>
              <div className="col-sm-6 mb-20">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-control radius-8"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="col-sm-6 mb-20" style={{ position: 'relative', zIndex: 1000 }}>
                <label className="form-label">Role</label>
                <CustomSelect
                  name="roleId"
                  value={roleId}
                  onChange={(selected) => setRoleId(selected?.value || "")}
                  options={roles}
                  placeholder="Select Role"
                  labelKey="role_name"
                  valueKey="role_id"
                  isLoading={rolesLoading}
                  isClearable={false}
                  addNewLabel="Add New Role"
                  addNewRoute="/add-role"
                  menuPlacement="auto"
                />
              </div>
              <div className="col-sm-6 mb-20" style={{ position: 'relative', zIndex: 999 }}>
                <label className="form-label">Status</label>
                <CustomSelect
                  name="status"
                  value={status}
                  onChange={(selected) => setStatus(selected?.value)}
                  options={statusOptions}
                  placeholder="Select Status"
                  labelKey="label"
                  valueKey="value"
                  isClearable={false}
                  isSearchable={false}
                  menuPlacement="auto"
                />
              </div>
              <div className="col-sm-12">
                {error && (
                  <div className="alert alert-danger alert-dismissible fade show">
                    {error}
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setError("")}
                    ></button>
                  </div>
                )}
                {success && (
                  <div className="alert alert-success alert-dismissible fade show">
                    {success}
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setSuccess("")}
                    ></button>
                  </div>
                )}
              </div>
              <div className="col-sm-12">
                <div className="d-flex align-items-center justify-content-center gap-3 mt-24">
                  <Link
                    to="/all-users"
                    className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-40 py-11 radius-8 text-decoration-none"
                  >
                    Cancel
                  </Link>
                  <button
                    type="button"
                    onClick={handleUpdateUser}
                    className="btn btn-primary-600 border border-primary-600 text-md px-24 py-12 radius-8"
                    disabled={updatedLoading}
                  >
                    {updatedLoading ? "Please wait..." : "Update User"}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditUser;